import { addYears, format, startOfYear } from 'date-fns';
import Page from './Page';
import Component from 'vue-class-component';
import FileHandler from '../common/FileHandler';
import GuestDatePicker from '../common/GuestDatePicker.vue';
import Countries from '../../master_data/countries.json';
import Prefectures from '../../master_data/prefectures.json';
import Occupations from '../../master_data/occupations.json';

type GuestData = {
    id: number;
    addr?: string;
    zip_code?: string;
    prefecture?: number | null;
    name?: string;
    name_kana?: string;
    country?: string;
    dob?: string;
    sex?: number;
    occupation_id?: number;
    occupation_free_input?: string;
    email?: string;
    phone?: string;
    form_memo?: string;
    lastplace?: string;
    destination?: string;
    passport_id?: string;
    passport_path?: boolean;
    face_path?: boolean;
    identification_path?: boolean;
};

type InvoiceRequest = {
    issue: boolean;
    guest_name: string;
    delivery: number;
    request: string;
};

type PhotoType = 'passport_path' | 'face_path' | 'identification_path';

const ItemsInPage = [
    ['country', 'name', 'name_kana', 'dob', 'sex', 'occupation'],
    ['email', 'phone', 'form_memo'],
    ['residency', 'zip_code', 'prefecture', 'addr'],
    [
        'lastplace',
        'destination',
        'passport_id',
        'passport_path',
        'face_path',
        'identification_path',
    ],
];

import FaceCheckStatus from './FaceCheckStatus.vue';
import LineModal from './LineModal.vue';
import Pointer from './Pointer.vue';
import HeaderComponent from './Header.vue';
import template from './Guest.vue.html';
import GuestRegisterHelper, {
    ItemSetting,
} from '../common/GuestRegisterHelper';
import { extractEncodable } from '../libs/Kana';
@Component({
    template,
    components: {
        Pointer,
        FaceCheckStatus,
        LineModal,
        GuestDatePicker,
        HeaderComponent,
    },
})
export default class Guest extends Page {
    readonly countries = Countries;
    readonly prefectures = Prefectures.filter(
        (prefecture) => prefecture.id != 99
    );
    readonly occupations = Occupations;

    private timer?: number;
    private worker?: number;

    items = {} as Record<string, ItemSetting<GuestData>>;
    previous?: GuestData;
    first: GuestData | null = null;
    guest: GuestData | null = null;
    invoice_issue_items: Record<string, boolean> | null = null;
    invoice_issue_request: InvoiceRequest | null = null;
    passport_path: string | null = null;
    face_path: string | null = null;
    identification_path: string | null = null;

    baseLineShareUrl: string =
        'https://social-plugins.line.me/lineit/share?url=';
    showLine: boolean = false;
    showShare: boolean = false;

    current_page = -1;

    guestLiveInJapan(): boolean {
        return this.guest?.prefecture !== 99;
    }
    setLiveInJapan() {
        if (this.guest?.prefecture === 99) {
            this.guest.prefecture = null;
            this.$forceUpdate();
        }
    }
    setLiveNotJapan() {
        if (this.guest) {
            this.guest.prefecture = 99;
            this.$forceUpdate();
        }
    }

    totalPages() {
        const pages = ItemsInPage.filter((list) =>
            list.find((key) => this.items[key])
        ).length;
        return this.invoice_issue_items ? pages + 1 : pages;
    }
    currentPageNo() {
        if (this.displayingInvoice()) return this.totalPages();
        return (
            ItemsInPage.filter((list) =>
                list.find((key) => this.items[key])
            ).indexOf(ItemsInPage[this.current_page]) + 1
        );
    }

    displayingInvoice() {
        console.log('current_page', this.current_page);
        return (
            this.invoice_issue_items && ItemsInPage.length == this.current_page
        );
    }

    private syncListener?: () => void;
    async created() {
        await this.next();
        this.syncListener = () => this.sync();
        document.addEventListener('blur', this.syncListener);

        this.worker = window.setInterval(() => {
            if (!document.hasFocus()) return;
            if (!this.timer) return;
            if (this.timer > Date.now()) return;
            this.sync();
        }, 1000);
    }

    destroyed() {
        this.worker && window.clearInterval(this.worker);
        this.syncListener &&
            document.removeEventListener('blur', this.syncListener);
        delete this.worker;
    }

    async sync() {
        const guest_id = this.app.query.guest_id;
        if (!guest_id) {
            this.app.setPage('Menu');
            return;
        }
        localStorage?.setItem('Mypage:ShownGuest:' + guest_id, 'true');
        try {
            const prop = {
                id: parseInt(guest_id),
                changed: null as Partial<GuestData> | null,
                invoice_issue_request: null as InvoiceRequest | null,
            };
            delete this.timer;

            const guest = this.guest;
            if (guest) {
                if (this.previous) {
                    const helper = new GuestRegisterHelper(this.items);
                    const changedColumns = [
                        ...helper.changedColumns(guest, this.previous ?? {}),
                    ];
                    if (this.validation(changedColumns).length > 0) return;
                    if (changedColumns.length > 0) {
                        prop.changed = Object.fromEntries(
                            changedColumns.map((key) => [key, guest[key]])
                        );
                    }
                }
                if (this.displayingInvoice()) {
                    prop.invoice_issue_request = this.invoice_issue_request;
                }
                if (!prop.changed && !prop.invoice_issue_request) return;
            }

            const r = await this.app.post('guest', prop);
            if (!r.succeed) throw new Error(r.error);

            this.items = r.items;
            this.invoice_issue_items = r.invoice_issue_items || null;
            this.invoice_issue_request = r.invoice_issue_request || null;
            this.previous = r.guest;
            this.guest = Object.assign({}, r.guest);
            this.first = r.first_guest;
        } catch (err) {
            console.error(err);
            const msg = err instanceof Error ? err.message : String(err);
            alert(msg || 'Connection Error');
            this.app.setPage('Menu');
        }
    }

    imageUploadStatus: string | null = null;
    async uploadImage(type: PhotoType, capture?: 'user' | 'environment') {
        const id = this.app.query.guest_id;
        if (!id) {
            this.app.setPage('Menu');
            return;
        }
        const file = await FileHandler.loadDialog({
            accept: 'image/*',
            capture,
        });
        if (!file) return;
        const photo = await FileHandler.resizeImage(file, 2048);

        this.imageUploadStatus = 'loading';
        try {
            const r = await this.app.post('upload-photo', { id, type, photo });
            if (!r.succeed) throw new Error(r.error);

            this[type] = photo;
            if (this.guest) this.guest[type] = true;

            this.imageUploadStatus = r.registered ? 'succeed' : null;
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            if (msg.includes('no_face') || msg.includes('too_faces')) {
                this.imageUploadStatus = msg;
                return;
            }
            console.error(err);
            this.imageUploadStatus = 'uncaught_error';
            alert(msg || 'Connection Error');
        }
    }

    changed() {
        if (this.displayingInvoice()) return;
        this.timer = Date.now() + 60_000;
    }

    async backMenu() {
        await this.sync();
        window.scroll(0, 0);
        if (this.guest)
            this.app.updateQuery({
                page: 'GuestInformation',
                guest_id: this.guest.id.toString(),
            });
        else this.app.setPage('Menu');
    }

    back() {
        while (true) {
            this.current_page--;
            const items = ItemsInPage[this.current_page];
            if (!items) return this.backMenu();
            if (items.find((k) => this.items[k])) break;
        }
    }

    private validation(keys: string[]) {
        const guest = this.guest;
        if (!guest) return [];
        const helper = new GuestRegisterHelper(this.items);
        return helper.validationAlerts(guest, keys, (t, ...a) =>
            this._(t, ...a)
        );
    }

    async next() {
        const invalids = this.validation(ItemsInPage[this.current_page] || []);
        if (invalids.length > 0) {
            alert(invalids.join('\n'));
            return;
        }

        await this.sync();
        if (this.displayingInvoice() && this.invoice_issue_request?.issue) {
            if (!this.invoice_issue_items?.delivery) {
                alert(this._('invoice_delivery'));
                return;
            }
        }

        while (true) {
            this.current_page++;
            if (this.displayingInvoice()) break;
            const items = ItemsInPage[this.current_page];
            if (!items) return this.backMenu();
            if (items.find((k) => this.items[k])) break;
        }

        window.scroll(0, 0);
    }

    fixKana(col: 'name_kana') {
        if (!this.guest) return;
        const str = this.guest[col];
        if (typeof str != 'string') return;
        this.guest[col] = str.replace(/[^ぁ-ゞァ-ヾｦ-ﾟA-Za-z\s]/g, '');
    }

    fixEncode(col: keyof GuestData) {
        if (!this.guest) return;
        const encode = this.app.house?.limit_encoding;
        if (!encode) return;
        const str = this.guest[col] as string;
        const extract = extractEncodable(str, encode);
        if (str != extract) {
            alert(this._('extracted_encoding'));
            (this.guest[col] as string) = extract;
        }
    }

    async checkComplete() {
        await this.sync();
        const invalids = this.validation(ItemsInPage.flat());
        if (invalids.length) {
            alert(invalids.join('\n'));
            return;
        }

        if (this.app.line && this.app.getLang() === 'ja') {
            const showedLineKey = `Mypage:ShownLine:${this.app.query.guest_id}`;
            const showedLine = !!localStorage?.getItem(showedLineKey);
            if (!showedLine) {
                this.showLine = true;
                localStorage?.setItem(showedLineKey, 'true');
                return;
            }
        }

        this.finish();
    }

    async finish() {
        const registeredAll = this.app.guestList.every(
            (g) => g.completed || g.id == this.guest?.id
        );
        if (registeredAll) {
            this.app.setPage('Terminal');
            return;
        }
        this.app.setPage('Menu');
    }

    isForeigner(guest: GuestData) {
        return guest.country != null && guest.country != 'JPN';
    }

    clickForeigner(guest: GuestData) {
        if (!this.isForeigner(guest)) guest.country = '';
    }

    async copyUrl() {
        await navigator.clipboard.writeText(location.href);
        alert(this._('copied_url'));
    }

    getShareUrl(): string {
        return this.baseLineShareUrl + encodeURIComponent(location.href);
    }

    sameToFirst<T extends keyof GuestData>(param: T) {
        if (!this.guest || !this.first) return;
        this.guest[param] = this.first[param];
    }

    sameToFirstAddress() {
        this.sameToFirst('prefecture');
        if (this.items.zip_code) this.sameToFirst('zip_code');
        if (this.items.addr) this.sameToFirst('addr');
    }

    defaultDob() {
        return format(addYears(startOfYear(new Date()), -30), 'yyyy-MM-dd');
    }

    async identifyAddr() {
        if (!this.guest?.zip_code) return;
        if (this.guest.addr && !confirm(this._('comfirm_overwrite_addr')))
            return;
        try {
            const r = await this.app.post('identify-addr', {
                zip_code: this.guest.zip_code,
            });
            if (!r.succeed) throw new Error(r.error);
            this.guest.addr = r.addr;
            this.guest.prefecture = r.prefecture;
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            console.error(err);
            alert(this._(msg) || 'Connection Error');
        }
        this.$forceUpdate();
    }
}
